import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from './api.js';
import { useSelfUser } from './selfUsers.js';

const Ctx = createContext({ cache: {}, seed: () => {}, fetchUser: async () => {} });

export function useUserCache() {
  return useContext(Ctx);
}

// Read from cache; trigger a fetch if this id has never been seen. Falls back to
// useSelfUser (see selfUsers.js) whenever the Context never resolves this id - the
// resilient path for a cross-bundle Context mismatch, same pattern as
// useResilientLookup for statuses/types/etc.
export function useUser(id) {
  const { cache, fetchUser } = useContext(Ctx);
  const ctxUser = id ? (cache[id] ?? null) : null;
  const { user: selfUser } = useSelfUser(id, !ctxUser);
  useEffect(() => {
    if (id && !cache[id]) fetchUser(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return ctxUser ?? selfUser;
}

export function UserCacheProvider({ children }) {
  const [cache, setCache] = useState({});
  const inflight = useRef(new Set()); // ids currently being fetched
  const misses   = useRef(new Set()); // ids that returned 404/error — don't retry

  // Bulk-seed from inline objects already present in an API response.
  // Normalises kind → role so existing components don't need changing.
  const seed = useCallback((users) => {
    setCache(prev => {
      const next = { ...prev };
      let changed = false;
      for (const raw of users) {
        if (!raw?.id || next[raw.id]) continue;
        next[raw.id] = { ...raw, role: raw.role ?? raw.kind };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  const fetchUser = useCallback(async (id) => {
    if (!id || inflight.current.has(id) || misses.current.has(id)) return;
    inflight.current.add(id);
    try {
      const data = await apiFetch(`/users/${id}`);
      if (data?.id) {
        setCache(prev => {
          if (prev[data.id]) return prev;
          return { ...prev, [data.id]: { ...data, role: data.role ?? data.kind } };
        });
      } else {
        misses.current.add(id);
      }
    } catch {
      misses.current.add(id); // 404 / network error — don't retry
    } finally {
      inflight.current.delete(id);
    }
  }, []);

  return <Ctx.Provider value={{ cache, seed, fetchUser }}>{children}</Ctx.Provider>;
}
